import { RECORD_ENTITY_LABELS, type NoteSource, type RecordEntity } from '@crm/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2, Plus } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api } from '../../lib/client.ts';
import { Button } from '../ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';
import { Textarea } from '../ui/input.tsx';
import { NOTE_SOURCE_KIND } from './note-origin-filter.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover.tsx';

const rowClass =
  'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-(--v2-dropdown-bg-hover)';

/** Kinds listed as a folding group in the tree; the others (the record, its company) are rows. */
const GROUPS: RecordEntity[] = ['contacts', 'deals', 'jobs', 'candidates'];

/**
 * "Add note" on the project's Overview, a menu like the stepper's Won/Lost buttons: it opens a
 * tree of where the note can go — the project and its company as rows, then Contacts, Deals,
 * Jobs and Candidates folding their records (as the Stage filter's tree) — and the pick opens
 * the dialog that writes the note there. Only the sources the viewer may write to are offered;
 * with none, there is no button.
 */
export function NoteCreateDialog({ sources }: { sources: readonly NoteSource[] }) {
  const qc = useQueryClient();
  const [menu, setMenu] = useState(false);
  const [unfolded, setUnfolded] = useState<ReadonlySet<RecordEntity>>(() => new Set());
  const [target, setTarget] = useState<NoteSource | null>(null);
  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = () => {
    setTarget(null);
    setBody('');
    setPinned(false);
    setError(null);
  };
  const create = useMutation({
    mutationFn: (t: NoteSource) =>
      api.notes.create({ entityType: t.entityType, entityId: t.entityId, body, pinned }),
    onSuccess: async () => {
      close();
      await qc.invalidateQueries({ queryKey: ['notes'] });
    },
    onError: (err) => setError(describeError(err)),
  });
  const writable = sources.filter((src) => src.canWrite);
  if (writable.length === 0) return null;
  const rows = writable.filter((src) => !GROUPS.includes(src.entityType));
  const groups = GROUPS.map((kind) => ({
    kind,
    items: writable.filter((src) => src.entityType === kind),
  })).filter((g) => g.items.length > 0);
  const fold = (kind: RecordEntity) =>
    setUnfolded((u) => {
      const next = new Set(u);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  const pick = (t: NoteSource) => {
    setMenu(false);
    setTarget(t);
  };

  return (
    <>
      <Popover open={menu} onOpenChange={setMenu}>
        <PopoverTrigger asChild>
          <Button size="sm" className="h-7 px-2 text-xs" data-testid="add-note">
            <Plus /> Add note <ChevronDown className="opacity-70" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-1.5" data-testid="note-target-menu">
          <ul aria-label="Add note to">
            {rows.map((src) => (
              <li key={`${src.entityType}:${src.entityId}`}>
                <button
                  type="button"
                  className={rowClass}
                  title={src.label}
                  onClick={() => pick(src)}
                >
                  <span className="min-w-0 truncate">
                    {src.entityType === 'projects'
                      ? 'Project'
                      : `${NOTE_SOURCE_KIND[src.entityType]}: ${src.label}`}
                  </span>
                </button>
              </li>
            ))}
            {groups.map(({ kind, items }) => {
              const open = unfolded.has(kind);
              return (
                <li key={kind}>
                  <button
                    type="button"
                    className={`${rowClass} justify-between`}
                    aria-expanded={open}
                    onClick={() => fold(kind)}
                  >
                    <span>
                      {RECORD_ENTITY_LABELS[kind]}{' '}
                      <span className="text-muted-foreground text-xs">{items.length}</span>
                    </span>
                    {open ? (
                      <ChevronDown className="size-4" />
                    ) : (
                      <ChevronRight className="size-4" />
                    )}
                  </button>
                  {open && (
                    <ul
                      aria-label={RECORD_ENTITY_LABELS[kind]}
                      className="max-h-60 overflow-auto pl-5"
                    >
                      {items.map((src) => (
                        <li key={src.entityId}>
                          <button
                            type="button"
                            className={rowClass}
                            title={src.label}
                            onClick={() => pick(src)}
                          >
                            <span className="min-w-0 truncate">{src.label}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>
      <Dialog open={target !== null} onOpenChange={(open) => !open && close()}>
        <DialogContent data-testid="note-create-dialog">
          <DialogHeader>
            <DialogTitle>New note</DialogTitle>
            <DialogDescription>
              On the {target && NOTE_SOURCE_KIND[target.entityType].toLowerCase()}
              {target?.entityType !== 'projects' && ` ${target?.label ?? ''}`}
            </DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (target && body.trim()) create.mutate(target);
            }}
          >
            <Textarea
              aria-label="New note"
              placeholder="Markdown or HTML: **bold**, <b>bold</b>, lists, links"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="max-h-80 min-h-32 overflow-y-auto"
              autoFocus
            />
            <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
                aria-label="Pin this note"
              />
              Pin to top
            </label>
            {error && (
              <p className="text-danger text-sm" role="alert">
                {error}
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!body.trim() || create.isPending}
                data-testid="save-note"
              >
                {create.isPending && <Loader2 className="animate-spin" />} Save note
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
